import { useEffect, useState } from 'react';
import { 
  Plus, Percent, DollarSign, Truck, Gift, Search,
  MoreHorizontal, Copy, Trash2, Edit, Calendar
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import api from '@/lib/api';

const typeIcons = {
  percentage: Percent,
  fixed_amount: DollarSign,
  free_shipping: Truck,
  bogo: Gift
};

const typeLabels = {
  percentage: 'Percentage Off',
  fixed_amount: 'Fixed Amount',
  free_shipping: 'Free Shipping',
  bogo: 'Buy One Get One'
};

export default function Discounts() {
  const [discounts, setDiscounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newDiscount, setNewDiscount] = useState({
    code: '',
    title: '',
    type: 'percentage',
    value: 10,
    minimum_requirement: 'none',
    minimum_value: 0,
    usage_limit_total: null,
    start_date: new Date().toISOString().split('T')[0],
    end_date: ''
  });

  useEffect(() => {
    fetchDiscounts();
  }, []);

  const fetchDiscounts = async () => {
    setLoading(true);
    try {
      const res = await api.get('/enterprise/discounts');
      setDiscounts(res.data.discounts);
    } catch (error) {
      toast.error('Failed to load discounts');
    } finally {
      setLoading(false);
    }
  };

  const createDiscount = async () => {
    try {
      if (!newDiscount.title) {
        toast.error('Please enter a title');
        return;
      }
      
      const payload = {
        ...newDiscount,
        start_date: new Date(newDiscount.start_date).toISOString(),
        end_date: newDiscount.end_date ? new Date(newDiscount.end_date).toISOString() : null
      };
      
      await api.post('/enterprise/discounts', payload);
      toast.success('Discount created successfully');
      setShowCreate(false);
      setNewDiscount({
        code: '',
        title: '',
        type: 'percentage',
        value: 10,
        minimum_requirement: 'none',
        minimum_value: 0,
        usage_limit_total: null,
        start_date: new Date().toISOString().split('T')[0],
        end_date: ''
      });
      fetchDiscounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create discount');
    }
  };

  const deleteDiscount = async (discountId) => {
    try {
      await api.delete(`/enterprise/discounts/${discountId}`);
      toast.success('Discount disabled');
      fetchDiscounts();
    } catch (error) {
      toast.error('Failed to delete discount');
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Code copied to clipboard');
  };

  const formatValue = (discount) => {
    if (discount.type === 'percentage') return `${discount.value}%`;
    if (discount.type === 'fixed_amount') return `SGD ${discount.value}`;
    if (discount.type === 'free_shipping') return 'Free';
    if (discount.type === 'bogo') return 'BOGO';
    return discount.value;
  };

  return (
    <div className="min-h-screen pb-16" data-testid="discounts-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-medium text-stone-900">Discounts</h1>
            <p className="text-stone-500 mt-1">Create and manage discount codes</p>
          </div>
          <Dialog open={showCreate} onOpenChange={setShowCreate}>
            <DialogTrigger asChild>
              <Button data-testid="create-discount-btn">
                <Plus className="h-4 w-4 mr-2" />
                Create Discount
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Create Discount</DialogTitle>
                <DialogDescription>
                  Create a new discount code for your customers
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Discount Code</Label>
                    <Input
                      value={newDiscount.code}
                      onChange={(e) => setNewDiscount({...newDiscount, code: e.target.value.toUpperCase()})}
                      placeholder="SAVE20"
                      data-testid="discount-code-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Title</Label>
                    <Input
                      value={newDiscount.title}
                      onChange={(e) => setNewDiscount({...newDiscount, title: e.target.value})}
                      placeholder="Summer Sale 20% Off"
                      data-testid="discount-title-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Discount Type</Label>
                    <Select 
                      value={newDiscount.type}
                      onValueChange={(v) => setNewDiscount({...newDiscount, type: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percentage">Percentage Off</SelectItem>
                        <SelectItem value="fixed_amount">Fixed Amount</SelectItem>
                        <SelectItem value="free_shipping">Free Shipping</SelectItem>
                        <SelectItem value="bogo">Buy One Get One</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Value</Label>
                    <div className="relative">
                      <Input
                        type="number"
                        value={newDiscount.value}
                        onChange={(e) => setNewDiscount({...newDiscount, value: parseFloat(e.target.value)})}
                        className="pr-8"
                        data-testid="discount-value-input"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-stone-500">
                        {newDiscount.type === 'percentage' ? '%' : 'SGD'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Minimum Requirement</Label>
                    <Select 
                      value={newDiscount.minimum_requirement}
                      onValueChange={(v) => setNewDiscount({...newDiscount, minimum_requirement: v})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No minimum</SelectItem>
                        <SelectItem value="amount">Minimum amount</SelectItem>
                        <SelectItem value="quantity">Minimum quantity</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {newDiscount.minimum_requirement !== 'none' && (
                    <div className="space-y-2">
                      <Label>Minimum Value</Label>
                      <Input
                        type="number"
                        value={newDiscount.minimum_value}
                        onChange={(e) => setNewDiscount({...newDiscount, minimum_value: parseFloat(e.target.value)})}
                      />
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Start Date</Label>
                    <Input
                      type="date"
                      value={newDiscount.start_date}
                      onChange={(e) => setNewDiscount({...newDiscount, start_date: e.target.value})}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>End Date (optional)</Label>
                    <Input
                      type="date"
                      value={newDiscount.end_date}
                      onChange={(e) => setNewDiscount({...newDiscount, end_date: e.target.value})}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Usage Limit (optional)</Label>
                  <Input
                    type="number"
                    value={newDiscount.usage_limit_total || ''}
                    onChange={(e) => setNewDiscount({
                      ...newDiscount, 
                      usage_limit_total: e.target.value ? parseInt(e.target.value) : null
                    })}
                    placeholder="Unlimited"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCreate(false)}>
                  Cancel
                </Button>
                <Button onClick={createDiscount} data-testid="save-discount-btn">
                  Create Discount
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg border p-4">
            <p className="text-2xl font-semibold">{discounts.length}</p>
            <p className="text-sm text-stone-500">Total Discounts</p>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <p className="text-2xl font-semibold text-green-600">
              {discounts.filter(d => d.status === 'active').length}
            </p>
            <p className="text-sm text-stone-500">Active</p>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <p className="text-2xl font-semibold text-yellow-600">
              {discounts.filter(d => d.status === 'scheduled').length}
            </p>
            <p className="text-sm text-stone-500">Scheduled</p>
          </div>
          <div className="bg-white rounded-lg border p-4">
            <p className="text-2xl font-semibold">
              {discounts.reduce((sum, d) => sum + (d.usage_count || 0), 0)}
            </p>
            <p className="text-sm text-stone-500">Total Uses</p>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Discount</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Value</TableHead>
                <TableHead>Usage</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Dates</TableHead>
                <TableHead className="w-[60px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                [...Array(5)].map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={7}>
                      <div className="h-12 skeleton rounded" />
                    </TableCell>
                  </TableRow>
                ))
              ) : discounts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12">
                    <Percent className="h-12 w-12 mx-auto text-stone-300 mb-4" />
                    <p className="text-stone-500">No discounts created yet</p>
                    <Button 
                      variant="outline" 
                      className="mt-4"
                      onClick={() => setShowCreate(true)}
                    >
                      Create your first discount
                    </Button>
                  </TableCell>
                </TableRow>
              ) : (
                discounts.map((discount) => {
                  const TypeIcon = typeIcons[discount.type] || Percent;
                  return (
                    <TableRow key={discount.discount_id}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{discount.title}</p>
                          {discount.code && (
                            <div className="flex items-center gap-2 mt-1">
                              <code className="text-xs bg-stone-100 px-2 py-0.5 rounded">
                                {discount.code}
                              </code>
                              <button 
                                onClick={() => copyCode(discount.code)}
                                className="text-stone-400 hover:text-stone-600"
                              >
                                <Copy className="h-3 w-3" />
                              </button>
                            </div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <TypeIcon className="h-4 w-4 text-stone-500" />
                          <span className="text-sm">{typeLabels[discount.type]}</span>
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">
                        {formatValue(discount)}
                      </TableCell>
                      <TableCell>
                        {discount.usage_count || 0}
                        {discount.usage_limit_total && ` / ${discount.usage_limit_total}`}
                      </TableCell>
                      <TableCell>
                        <Badge className={
                          discount.status === 'active' ? 'bg-green-100 text-green-800' :
                          discount.status === 'scheduled' ? 'bg-yellow-100 text-yellow-800' :
                          discount.status === 'expired' ? 'bg-gray-100 text-gray-800' :
                          'bg-red-100 text-red-800'
                        }>
                          {discount.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-stone-500">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(discount.start_date).toLocaleDateString()}
                          {discount.end_date && ` - ${new Date(discount.end_date).toLocaleDateString()}`}
                        </div>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem>
                              <Edit className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <Copy className="h-4 w-4 mr-2" />
                              Duplicate
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              className="text-red-600"
                              onClick={() => deleteDiscount(discount.discount_id)}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Disable
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}
